import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BROWSER_COLORS, Button, Favicon, Icon, Modal, Skeleton, Tag, Toast } from '@vault/ui';
import type { BrowserName } from '@vault/ui';
import type { Item, Sweep } from '@vault/shared';
import { ApiError, api } from '../../api/client.js';
import { deriveBrowser, domainOf, formatDate, formatDateTime, readingTime } from '../timeline/helpers.js';
import { VersionHistory } from './VersionHistory.js';

export interface PageDetailProps {
  item: Item;
  /** the sweep the item was opened from — used for the "saved from" date */
  sweep: Sweep;
  onClose: () => void;
}

/**
 * Right-side slide-over for a single saved page: metadata + reader view of the
 * captured snapshot. Content is fetched lazily; a missing snapshot (404) is a
 * friendly empty state, not an error.
 */
export function PageDetail({ item, sweep, onClose }: PageDetailProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [tab, setTab] = useState<'reader' | 'history'>('reader');

  const usageQ = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const isPro = usageQ.data?.plan === 'pro';

  const browser: BrowserName = deriveBrowser(sweep.deviceId);
  const dot = BROWSER_COLORS[browser];
  const domain = domainOf(item.url);

  const contentQ = useQuery({
    queryKey: ['content', item.id],
    queryFn: () => api.vault.content(item.id),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
  });

  const del = useMutation({
    mutationFn: () => api.vault.deleteItem(item.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sweeps'] });
      void qc.invalidateQueries({ queryKey: ['sweep', item.sweepId] });
      void qc.invalidateQueries({ queryKey: ['usage'] });
      onClose();
    },
  });

  // Esc closes the panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  function openLive(): void {
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }
  function copyUrl(): void {
    void navigator.clipboard?.writeText(item.url);
    setCopied(true);
  }

  const notFound = contentQ.error instanceof ApiError && contentQ.error.status === 404;

  return (
    <div className="pdv-scrim" onClick={onClose}>
      <style>{PDV_CSS}</style>
      <div className="pdv-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={item.title || domain}>
        {/* panel top bar */}
        <div className="pdv-topbar">
          <span className="pdv-back" onClick={onClose}>
            <Icon name="chevron-left" size={13} />
            Back to timeline
          </span>
          <span className="pdv-urlpill">
            <Icon name="lock" size={10} color="var(--success)" />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.url.replace(/^https?:\/\//, '')}</span>
          </span>
          <span className="pdv-x" aria-label="Close" onClick={onClose}>
            <Icon name="x" size={11} />
          </span>
        </div>

        <div className="pdv-body">
          {/* metadata column */}
          <div className="pdv-meta">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Favicon letter={(domain.charAt(0) || '?').toUpperCase()} index={0} size={34} ring="transparent" />
              <span style={{ fontFamily: 'var(--font-display-cond)', fontSize: 19, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', lineHeight: 1.25 }}>
                {item.title || domain}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 9, padding: '8px 11px' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                  {item.url.replace(/^https?:\/\//, '')}
                </span>
                <span onClick={copyUrl} aria-label="Copy URL" style={{ flexShrink: 0, cursor: 'pointer', color: 'var(--text-3)', display: 'inline-flex' }}>
                  <CopyGlyph />
                </span>
              </span>
            </div>

            {/* metadata rows */}
            <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--surface-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)' }}>
              <MetaRow label="SAVED FROM">
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <span style={{ width: 5.5, height: 5.5, borderRadius: '50%', background: dot }} />
                  {browser.charAt(0).toUpperCase() + browser.slice(1)} · {formatDateTime(sweep.clientCreatedAt)}
                </span>
              </MetaRow>
              {item.groupName && (
                <MetaRow label="TAB GROUP">
                  <span style={{ fontSize: 12 }}>{item.groupName}</span>
                </MetaRow>
              )}
              <MetaRow label="SNAPSHOT" last>
                <span style={{ fontSize: 12 }}>
                  {contentQ.isPending ? (
                    <Skeleton width={120} height={11} />
                  ) : notFound || !contentQ.data ? (
                    'No snapshot captured'
                  ) : (
                    `Text${contentQ.data.content.lang ? ` · ${contentQ.data.content.lang.toUpperCase()}` : ''} · ${contentQ.data.content.wordCount.toLocaleString()} words`
                  )}
                </span>
              </MetaRow>
            </div>

            {/* tags */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>TAGS</span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                <Tag tone="neutral">{domain}</Tag>
                {item.groupName && <Tag tone="neutral">{item.groupName}</Tag>}
                {item.pinned && <Tag tone="primary">pinned</Tag>}
              </span>
            </div>

            {/* actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 'auto', paddingTop: 8 }}>
              <Button variant="primary" fullWidth onClick={openLive} icon={<Icon name="external" size={13} color="currentColor" />}>
                Open live page
              </Button>
              <Button variant="secondary" fullWidth onClick={openLive} icon={<Icon name="restore" size={13} color="currentColor" />}>
                Restore this tab
              </Button>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <Button variant="ghost" fullWidth onClick={copyUrl} icon={<CopyGlyph />}>
                    Copy URL
                  </Button>
                </div>
                <div style={{ flex: 1 }}>
                  <Button variant="danger" fullWidth onClick={() => setConfirmDel(true)} icon={<Icon name="trash" size={13} color="currentColor" />}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* reader / history column */}
          <div className="pdv-reader">
            <div className="pdv-reader-head" role="tablist" aria-label="Page views">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'reader'}
                className={`pdv-tab${tab === 'reader' ? ' is-active' : ''}`}
                onClick={() => setTab('reader')}
              >
                <Icon name="list" size={12} color="currentColor" />
                Reader view
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'history'}
                className={`pdv-tab${tab === 'history' ? ' is-active' : ''}`}
                onClick={() => setTab('history')}
              >
                <Icon name="clock" size={12} color="currentColor" />
                History
                {!isPro && <span className="pdv-tab-pro">PRO</span>}
              </button>
              <span style={{ flex: 1 }} />
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.14em', color: 'var(--text-3)' }}>
                SAVED {formatDate(sweep.clientCreatedAt).toUpperCase()}
              </span>
            </div>

            {tab === 'reader' ? (
              <>
                <div className="pdv-reader-body">
                  {contentQ.isPending && <ReaderSkeleton />}

                  {!contentQ.isPending && notFound && (
                    <ReaderEmpty
                      title="No snapshot captured"
                      body="This tab was saved as a link only — there's no reader copy to show. Open the live page to read it now."
                      onOpen={openLive}
                    />
                  )}

                  {!contentQ.isPending && !notFound && contentQ.isError && (
                    <ReaderEmpty
                      title="Couldn't load the snapshot"
                      body="Something went wrong fetching the saved page. Check your connection and try again."
                      actionLabel="Try again"
                      onOpen={() => void contentQ.refetch()}
                    />
                  )}

                  {!contentQ.isPending && !contentQ.isError && contentQ.data && (
                    <Reader item={item} domain={domain} content={contentQ.data.content} />
                  )}
                </div>
                <div className="pdv-reader-fade" />
              </>
            ) : (
              <div className="pdv-reader-body" style={{ display: 'block', padding: '20px 32px', overflow: 'auto' }}>
                <VersionHistory itemId={item.id} isPro={isPro} onUpgrade={() => navigate('/billing')} />
              </div>
            )}
          </div>
        </div>
      </div>

      {confirmDel && (
        <Modal
          title="Delete this page?"
          subtitle="This removes the saved tab and its snapshot from your vault. This can't be undone."
          onClose={() => setConfirmDel(false)}
          width={420}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmDel(false)}>
                Cancel
              </Button>
              <Button variant="danger" loading={del.isPending} onClick={() => del.mutate()}>
                Delete page
              </Button>
            </>
          }
        >
          <span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{item.title || domain}</span>
        </Modal>
      )}

      {copied && <Toast tone="success" title="Link copied" description="The page URL is on your clipboard." />}
    </div>
  );
}

// ── reader sub-views ────────────────────────────────────────────────────────

interface ReaderProps {
  item: Item;
  domain: string;
  content: { text: string; excerpt: string; lang: string | null; wordCount: number; capturedAt: number };
}

function Reader({ item, domain, content }: ReaderProps) {
  const paragraphs = (content.text || content.excerpt || '')
    .split(/\n{1,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  const lead = content.excerpt && paragraphs[0] !== content.excerpt ? content.excerpt : null;

  return (
    <div className="pdv-article">
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--amber-ink)', textTransform: 'uppercase' }}>
        {domain}
        {content.lang ? ` · ${content.lang.toUpperCase()}` : ''}
      </span>
      <span style={{ fontFamily: 'var(--font-display-cond)', fontSize: 30, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase', lineHeight: 1.18 }}>
        {item.title || domain}
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-3)' }}>
        {content.wordCount.toLocaleString()} WORDS · {readingTime(content.wordCount)} MIN READ · SAVED {formatDate(content.capturedAt).toUpperCase()}
      </span>
      {lead && <p style={{ margin: 0, fontSize: 15, lineHeight: 1.75, color: 'var(--text-1)' }}>{lead}</p>}
      {paragraphs.length === 0 && !lead && (
        <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--text-3)' }}>The captured text for this page is empty.</p>
      )}
      {paragraphs.map((p, i) => (
        <p key={i} style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--text-2)' }}>
          {p}
        </p>
      ))}
    </div>
  );
}

function ReaderSkeleton() {
  return (
    <div className="pdv-article">
      <Skeleton width={180} height={10} />
      <Skeleton width="80%" height={26} />
      <Skeleton width={220} height={10} />
      {[92, 96, 88, 94, 70].map((w, i) => (
        <Skeleton key={i} width={`${w}%`} height={13} />
      ))}
    </div>
  );
}

interface ReaderEmptyProps {
  title: string;
  body: string;
  actionLabel?: string;
  onOpen: () => void;
}

function ReaderEmpty({ title, body, actionLabel, onOpen }: ReaderEmptyProps) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: '48px 24px' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, borderRadius: 16, background: 'var(--surface-1)', border: '1px solid var(--line)' }}>
        <Icon name="external" size={20} color="var(--text-3)" />
      </span>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600 }}>{title}</span>
      <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)', maxWidth: 340 }}>{body}</span>
      <Button variant="secondary" onClick={onOpen}>
        {actionLabel ?? 'Open live page'}
      </Button>
    </div>
  );
}

function MetaRow({ label, children, last = false }: { label: string; children: React.ReactNode; last?: boolean }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', padding: '10px 13px', borderBottom: last ? 'none' : '1px solid var(--line)' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.12em', color: 'var(--text-3)', width: 96, flexShrink: 0 }}>{label}</span>
      {children}
    </span>
  );
}

function CopyGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <rect x="1.8" y="4.8" width="9.4" height="9.4" rx="2.4" />
      <path d="M5 4.4V4a2.2 2.2 0 0 1 2.2-2.2H12A2.2 2.2 0 0 1 14.2 4v4.8A2.2 2.2 0 0 1 12 11h-.4" />
    </svg>
  );
}

const PDV_CSS = `
.pdv-scrim{position:fixed;inset:0;background:var(--overlay);z-index:50;display:flex;justify-content:flex-end;font-family:var(--font-body)}
.pdv-panel{width:min(1040px,96vw);height:100%;background:var(--bg);border-left:1px solid var(--line-strong);box-shadow:var(--shadow-modal);display:flex;flex-direction:column;animation:pdv-in .24s cubic-bezier(.22,.61,.36,1)}
.pdv-topbar{display:flex;align-items:center;gap:10px;height:48px;flex-shrink:0;padding:0 16px;border-bottom:1px solid var(--line);background:var(--surface-0)}
.pdv-back{display:inline-flex;align-items:center;gap:7px;font-size:12.5px;font-weight:600;color:var(--text-2);cursor:pointer}
.pdv-back:hover{color:var(--text-1)}
.pdv-urlpill{margin:0 auto;display:inline-flex;align-items:center;gap:7px;max-width:min(46%,420px);border:1px solid var(--line);border-radius:999px;padding:5px 14px;font-family:var(--font-mono);font-size:10.5px;color:var(--text-2)}
.pdv-x{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;border:1px solid var(--line-strong);color:var(--text-3);cursor:pointer}
.pdv-x:hover{background:var(--surface-2);color:var(--text-1)}
.pdv-body{flex:1;display:flex;min-height:0}
.pdv-meta{width:380px;flex-shrink:0;border-right:1px solid var(--line);background:var(--surface-0);display:flex;flex-direction:column;gap:16px;padding:20px 22px;overflow:auto}
.pdv-reader{flex:1;min-width:0;position:relative;display:flex;flex-direction:column;min-height:0}
.pdv-reader-head{display:flex;align-items:center;gap:16px;padding:10px 32px;border-bottom:1px solid var(--line);flex-shrink:0}
.pdv-tab{display:inline-flex;align-items:center;gap:6px;background:none;border:none;font-family:var(--font-mono);font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--text-3);cursor:pointer;padding:6px 2px;border-bottom:2px solid transparent}
.pdv-tab:hover{color:var(--text-2)}
.pdv-tab.is-active{color:var(--text-1);border-bottom-color:var(--primary)}
.pdv-tab-pro{font-size:8px;font-weight:700;color:var(--amber-ink);background:var(--amber-tint);border:1px solid var(--amber-line);border-radius:4px;padding:0 4px;letter-spacing:.08em}
.pdv-reader-body{flex:1;overflow:auto;display:flex;justify-content:center;padding:36px 0}
.pdv-article{width:min(640px,90%);display:flex;flex-direction:column;gap:18px}
.pdv-reader-fade{position:absolute;left:0;right:0;bottom:0;height:56px;background:linear-gradient(transparent,var(--bg));pointer-events:none}
@keyframes pdv-in{from{transform:translateX(28px);opacity:.3}to{transform:translateX(0);opacity:1}}
@media (max-width:860px){
  .pdv-panel{width:100vw;border-left:none}
  .pdv-urlpill{display:none}
  .pdv-body{flex-direction:column;overflow:auto}
  .pdv-meta{width:auto;border-right:none;border-bottom:1px solid var(--line);overflow:visible}
  .pdv-reader{overflow:visible;min-height:auto}
  .pdv-reader-body{overflow:visible;padding:24px 0}
  .pdv-reader-fade{display:none}
}
`;
